import BusinessOutlined from '@mui/icons-material/BusinessOutlined'
import { Box, Button, Chip, Theme, Typography } from '@mui/material'
import { MoneyString } from 'model/Money'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityOutsourcedStatus,
  BomItemActivityOutsourcedDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'
import { getOutsourceStatusColor } from './workingStepOutsourceStatus'

const useStyles = makeStyles({ name: 'WorkingStepOutsourceDetails' })(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.action.hover,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      width: '100%',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
    },
  }),
)

type Props = {
  outsourceDetails?: BomItemActivityOutsourcedDto
  onOpenRequestForQuotation?: () => void
}

function getStatusLabel(
  status: BoMItemActivityOutsourcedStatus,
  t: ReturnType<typeof useTranslation>['t'],
) {
  switch (status) {
    case BoMItemActivityOutsourcedStatus.New:
      return t('project:outsourced-status-new', 'new')
    case BoMItemActivityOutsourcedStatus.DocumentCreated:
      return t('project:outsourced-status-document-created', 'document created')
    case BoMItemActivityOutsourcedStatus.RequestForQuotationSent:
      return t('project:outsourced-status-rfq-sent', 'RFQ sent')
    case BoMItemActivityOutsourcedStatus.RequestForQuotationReceived:
      return t('project:outsourced-status-rfq-received', 'RFQ received')
    case BoMItemActivityOutsourcedStatus.RequestForQuotationReceivedError:
      return t('project:outsourced-status-rfq-error', 'RFQ error')
    case BoMItemActivityOutsourcedStatus.QuotationResponseReceived:
      return t(
        'project:outsourced-status-quotation-response-received',
        'quotation received',
      )
    case BoMItemActivityOutsourcedStatus.Accepted:
      return t('project:outsourced-status-accepted', 'accepted')
    default:
      return status
  }
}

export function WorkingStepOutsourceDetails({
  outsourceDetails,
  onOpenRequestForQuotation,
}: Props) {
  const { classes } = useStyles()
  const { t } = useTranslation()

  const isOutsourced = Boolean(outsourceDetails)
  const supplierLabel =
    outsourceDetails?.supplierName || outsourceDetails?.supplierId
  const outsourcePrice = outsourceDetails?.price
    ? MoneyString(outsourceDetails.price, 2)
    : undefined
  const outsourceStatus = outsourceDetails?.status

  if (!isOutsourced) {
    return null
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Chip
          size="small"
          color={isOutsourced ? 'info' : 'default'}
          variant={isOutsourced ? 'filled' : 'outlined'}
          icon={isOutsourced ? <BusinessOutlined /> : undefined}
          label={
            isOutsourced
              ? t('project:working-step-outsourced', 'outsourced')
              : t('project:working-step-not-outsourced', 'not outsourced')
          }
        />
        {outsourceStatus && (
          <Chip
            size="small"
            color={getOutsourceStatusColor(outsourceStatus)}
            variant="outlined"
            label={getStatusLabel(outsourceStatus, t)}
          />
        )}
      </Box>
      {isOutsourced && (
        <>
          {supplierLabel && (
            <Box className={classes.detailRow}>
              <Typography variant="caption" color="textSecondary">
                {t('common:supplier', 'supplier')}
              </Typography>
              <Typography variant="caption" color="textPrimary">
                {supplierLabel}
              </Typography>
            </Box>
          )}
          {outsourcePrice && (
            <Box className={classes.detailRow}>
              <Typography variant="caption" color="textSecondary">
                {t('project:outsourced-price', 'outsourced price')}
              </Typography>
              <Typography variant="caption" color="textPrimary">
                {outsourcePrice}
              </Typography>
            </Box>
          )}
          {outsourceDetails?.requestForQuotationId &&
            onOpenRequestForQuotation && (
              <Box className={classes.detailRow}>
                <Button
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={onOpenRequestForQuotation}
                >
                  {t('project:open-request-for-quotation', 'open RFQ')}
                </Button>
              </Box>
            )}
        </>
      )}
    </Box>
  )
}
